import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronRight, Phone, Check, Percent, DollarSign, CalendarCheck, ShieldCheck, Car, Building2, Users, Wrench, MapPin, Settings2, Repeat } from "lucide-react";
import { Seo } from "@/components/Seo";
import { BELO_PROVIDER } from "@/data/cityLocations";
import { HOST_LANDING_PAGES, HOST_RELATED_LINKS } from "@/data/hostLandingPages";
import { HOST_LANDING_PT, PT_SLUG_MAP, HOST_RELATED_LINKS_PT } from "@/data/hostLandingPagesPt";
import { HOST_GUIDES } from "@/data/hostGuides";
import { track } from "@/lib/analytics";

const SITE = "https://belorentals.com";

const ICONS = { Percent, DollarSign, CalendarCheck, ShieldCheck, Car, Building2, Users, Wrench, MapPin, Settings2, Repeat };

const GUIDES_BY_SLUG = {
  "rent-out-your-car-massachusetts": ["how-hosting-works", "how-to-price-your-car-for-rental-in-massachusetts", "what-insurance-massachusetts-car-hosts-need"],
  "no-host-fee-car-rental-marketplace": ["understanding-host-payouts-and-taxes-in-massachusetts", "how-to-price-your-car-for-rental-in-massachusetts", "how-to-get-more-bookings-as-a-car-host"],
  "turo-alternative-for-hosts": ["how-hosting-works", "how-to-get-more-bookings-as-a-car-host", "getting-great-reviews-as-a-host"],
  "self-managed-car-hosting": ["preparing-your-car-for-guest-handoff", "cleaning-and-turnaround-checklist-for-hosts", "guest-communication-tips-for-hosts"],
  "managed-car-hosting": ["managing-multiple-cars-efficiently", "budgeting-for-maintenance-and-depreciation", "keeping-your-listing-ranked"],
  "fleet-partnerships": ["managing-multiple-cars-efficiently", "choosing-the-right-car-to-buy-for-hosting", "budgeting-for-maintenance-and-depreciation"],
  "how-hosting-works": ["first-30-days-as-a-new-host", "writing-a-listing-that-books", "how-to-photograph-your-car-for-more-bookings"],
  "host-earnings": ["how-to-price-your-car-for-rental-in-massachusetts", "understanding-host-payouts-and-taxes-in-massachusetts", "seasonal-car-rental-demand-in-boston"],
  "host-requirements": ["massachusetts-registration-and-rules-for-car-hosts", "hosting-a-financed-or-leased-car", "hosting-an-electric-vehicle-in-massachusetts"],
  "host-insurance": ["what-insurance-massachusetts-car-hosts-need", "how-to-handle-damage-and-claims-as-a-host", "building-a-claim-documentation-kit"],
  "host-payouts": ["understanding-host-payouts-and-taxes-in-massachusetts", "how-to-price-your-car-for-rental-in-massachusetts", "budgeting-for-maintenance-and-depreciation"],
  "belo-rentals-vs-turo-for-hosts": ["how-hosting-works", "how-to-get-more-bookings-as-a-car-host", "understanding-host-payouts-and-taxes-in-massachusetts"],
  "weekly-car-rental-hosting": ["setting-mileage-and-travel-limits", "off-season-hosting-strategies", "how-to-price-your-car-for-rental-in-massachusetts"],
  "long-term-car-rental-hosting": ["off-season-hosting-strategies", "budgeting-for-maintenance-and-depreciation", "setting-mileage-and-travel-limits"],
  "rent-out-multiple-cars": ["managing-multiple-cars-efficiently", "budgeting-for-maintenance-and-depreciation", "choosing-the-right-car-to-buy-for-hosting"],
  "dealership-rental-partnerships": ["managing-multiple-cars-efficiently", "choosing-the-right-car-to-buy-for-hosting", "keeping-your-listing-ranked"],
};
const DEFAULT_GUIDES = ["how-hosting-works", "how-to-price-your-car-for-rental-in-massachusetts", "what-insurance-massachusetts-car-hosts-need"];

const T = {
  en: { apply: "Apply to become a host", calc: "Calculate earnings", faqTitle: "Frequently asked questions",
    explore: "Explore hosting with Belo Rentals", ctaTitle: "Set your price. Keep your listed rate.",
    ctaSub: "Most complete applications are reviewed within 24 hours.",
    insurance: "Hosts maintain their own insurance and are responsible for claims. Belo Rentals does not provide host insurance or physical-damage protection." },
  pt: { apply: "Candidate-se para ser anfitrião", calc: "Calcular ganhos", faqTitle: "Perguntas frequentes",
    explore: "Conheça a hospedagem com a Belo Rentals", ctaTitle: "Defina o seu preço. Receba a tarifa cheia.",
    ctaSub: "A maioria das candidaturas completas é analisada em até 24 horas.",
    insurance: "Os anfitriões mantêm o próprio seguro e são responsáveis por sinistros. A Belo Rentals não fornece seguro ao anfitrião nem proteção contra danos." },
};

// Merge PT copy onto the base page (keeping icons from the English registry).
function ptContent(enSlug) {
  const base = HOST_LANDING_PAGES[enSlug];
  const pt = HOST_LANDING_PT[enSlug];
  if (!base || !pt) return null;
  return { ...base, ...pt, highlights: base.highlights.map((h, i) => ({ icon: h.icon, ...(pt.highlights?.[i] || {}) })) };
}

export default function HostLanding({ slug, lang = "en" }) {
  const cfg = lang === "pt" ? ptContent(slug) : HOST_LANDING_PAGES[slug];
  const [openFaq, setOpenFaq] = useState(0);
  if (!cfg) return null;

  const tx = T[lang] || T.en;
  const enPath = `/${slug}`;
  const ptPath = PT_SLUG_MAP[slug] ? `/pt/${PT_SLUG_MAP[slug]}` : null;
  const selfPath = lang === "pt" ? ptPath : enPath;
  const related = lang === "pt" ? HOST_RELATED_LINKS_PT : HOST_RELATED_LINKS;
  const alternates = ptPath ? [
    { lang: "en-US", path: enPath },
    { lang: "pt-BR", path: ptPath },
    { lang: "x-default", path: enPath },
  ] : null;

  const faqLd = { "@context": "https://schema.org", "@type": "FAQPage",
    mainEntity: cfg.faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) };
  const crumbLd = { "@context": "https://schema.org", "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
      { "@type": "ListItem", position: 2, name: "Become a Host", item: `${SITE}/become-a-host` },
      { "@type": "ListItem", position: 3, name: cfg.h1, item: `${SITE}${selfPath}` },
    ] };
  const serviceLd = { "@context": "https://schema.org", "@type": "Service", serviceType: "Car rental host marketplace",
    name: cfg.h1, areaServed: { "@type": "State", name: "Massachusetts" }, provider: { ...BELO_PROVIDER } };

  return (
    <div data-testid={`host-landing-${slug}`} className="bg-white">
      <Seo title={cfg.metaTitle} description={cfg.metaDescription} path={selfPath} keywords={cfg.keywords} jsonLd={[faqLd, crumbLd, serviceLd]} alternates={alternates} />

      {/* Hero */}
      <section className="relative pt-32 pb-16 px-5 sm:px-8 bg-slate-900 text-white overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(60%_50%_at_50%_0%,rgba(37,99,235,0.22),transparent)]" />
        <div className="relative max-w-4xl mx-auto text-center">
          <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-blue-500/15 text-blue-200 text-sm font-medium mb-6">{cfg.badge}</span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight">{cfg.h1}</h1>
          <p className="mt-6 text-lg text-slate-300 max-w-2xl mx-auto leading-relaxed">{cfg.intro}</p>
          <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
            <Link to="/host" onClick={() => track("application_started", { page: slug, lang })} data-testid="host-landing-apply" className="px-7 py-3.5 rounded-full bg-blue-600 text-white font-semibold hover:bg-blue-700 transition-colors inline-flex items-center gap-2">{tx.apply} <ChevronRight size={18} /></Link>
            <Link to="/become-a-host#calculator" className="px-7 py-3.5 rounded-full border border-white/25 text-white font-semibold hover:bg-white/10 transition-colors">{tx.calc}</Link>
            <a href="tel:+19782211451" onClick={() => track("host_phone_click", { page: slug, lang })} className="px-7 py-3.5 rounded-full border border-white/25 text-white font-semibold hover:bg-white/10 transition-colors inline-flex items-center gap-2"><Phone size={16} /> 978-221-1451</a>
          </div>
        </div>
      </section>

      {/* Highlights */}
      <section className="py-16 px-5 sm:px-8 max-w-6xl mx-auto">
        <div className="grid sm:grid-cols-3 gap-5">
          {cfg.highlights.map((h, i) => (
            <div key={i} className="bg-slate-50 rounded-2xl p-6 border border-slate-100">
              <span className="grid place-items-center w-11 h-11 rounded-xl bg-blue-600 text-white mb-4">{(() => { const Ic = ICONS[h.icon]; return Ic ? <Ic size={20} /> : null; })()}</span>
              <h3 className="font-heading font-bold text-slate-900">{h.title}</h3>
              <p className="text-slate-600 text-sm mt-1.5 leading-relaxed">{h.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Content sections */}
      <section className="pb-8 px-5 sm:px-8 max-w-3xl mx-auto space-y-8">
        {cfg.sections.map((s, i) => (
          <div key={i}>
            <h2 className="font-heading text-2xl font-bold text-slate-900">{s.heading}</h2>
            <p className="text-slate-600 mt-3 leading-relaxed">{s.body}</p>
          </div>
        ))}
        <p className="flex items-start gap-2 text-slate-700 text-sm bg-amber-50 border border-amber-100 rounded-xl p-4">
          <Check size={16} className="text-amber-600 mt-0.5 shrink-0" /> {tx.insurance}
        </p>
        {cfg.trademark && <p className="text-xs text-slate-400">{cfg.trademark}</p>}
      </section>

      {/* FAQ */}
      <section className="py-16 px-5 sm:px-8 bg-slate-50">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-3xl font-bold text-slate-900 text-center">{tx.faqTitle}</h2>
          <div className="mt-10 space-y-3">
            {cfg.faqs.map((f, i) => (
              <div key={i} data-testid={`host-landing-faq-${i}`} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <button onClick={() => setOpenFaq(openFaq === i ? -1 : i)} className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left">
                  <span className="font-medium text-slate-900">{f.q}</span>
                  <ChevronDown size={18} className={`shrink-0 text-slate-400 transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
                </button>
                {openFaq === i && <div className="px-5 pb-5 text-slate-600 text-sm leading-relaxed">{f.a}</div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related host guides (EN only — guides are English) */}
      {lang !== "pt" && (
        <section className="pb-6 px-5 sm:px-8 max-w-6xl mx-auto">
          <h2 className="font-heading text-2xl font-bold text-slate-900 text-center">Related host guides</h2>
          <div className="grid sm:grid-cols-3 gap-5 mt-8" data-testid="host-related-guides">
            {(GUIDES_BY_SLUG[slug] || DEFAULT_GUIDES).filter((g) => HOST_GUIDES[g]).map((g) => (
              <Link key={g} to={`/host-guides/${g}`} className="group bg-slate-50 rounded-2xl p-6 border border-slate-100 hover:border-blue-300 hover:shadow-sm transition-all">
                <h3 className="font-heading font-bold text-slate-900 group-hover:text-blue-700 transition-colors">{HOST_GUIDES[g].h1}</h3>
                <p className="text-slate-600 text-sm mt-2 leading-relaxed">{HOST_GUIDES[g].excerpt}</p>
                <span className="mt-3 inline-flex items-center gap-1 text-blue-600 text-sm font-semibold">Read guide <ChevronRight size={15} /></span>
              </Link>
            ))}
          </div>
          <div className="text-center mt-6">
            <Link to="/host-guides" className="inline-flex items-center gap-1.5 text-blue-600 font-semibold text-sm hover:text-blue-700">Browse all host guides <ChevronRight size={16} /></Link>
          </div>
        </section>
      )}

      {/* Related host links */}
      <section className="py-14 px-5 sm:px-8 max-w-6xl mx-auto">
        <h2 className="font-heading text-2xl font-bold text-slate-900 text-center">{tx.explore}</h2>
        <div className="mt-8 flex flex-wrap justify-center gap-3" data-testid="host-related-links">
          {related.filter(([to]) => to !== selfPath).map(([to, label]) => (
            <Link key={to} to={to} className="px-4 py-2 rounded-full bg-slate-50 border border-slate-200 text-slate-700 text-sm font-medium hover:border-blue-400 hover:text-blue-700 transition-colors">{label}</Link>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="pb-20 px-5 sm:px-8">
        <div className="max-w-4xl mx-auto rounded-3xl bg-gradient-to-r from-blue-600 to-blue-700 text-white text-center px-8 py-14">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold">{tx.ctaTitle}</h2>
          <p className="mt-4 text-blue-50 max-w-xl mx-auto">{tx.ctaSub}</p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link to="/host" onClick={() => track("application_started", { page: `${slug}_cta`, lang })} className="px-8 py-3.5 rounded-full bg-white text-blue-700 font-semibold hover:bg-blue-50 transition-colors inline-flex items-center gap-2">{tx.apply} <ChevronRight size={18} /></Link>
            <a href="tel:+19782211451" className="px-8 py-3.5 rounded-full border border-white/30 text-white font-semibold hover:bg-white/10 transition-colors inline-flex items-center gap-2"><Phone size={16} /> 978-221-1451</a>
          </div>
        </div>
      </section>
    </div>
  );
}
